import PropTypes from 'prop-types'
import styled, { theme, prop } from '@nobia/zeus-components/lib/styled'
import React, { Suspense, useState, useEffect } from 'react'

import Accordion from '../accordion/Accordion'
import Cards from '../cards/Cards'
import Loader from '../loader/Loader'
import OpenCloseIcon from './OpenCloseIcon'
import Summary from '../summary/Summary'
import { formatPrice } from '../cards/utils'
import { getMonthlyPayment, getMonthlyPaymentShortVersion, hasFullVersion, hasMonthlyPayment, hasMagnetSimplifiedVersion } from '../../brands/monthsToPay'
import { GUEST_ACCESS_EVENT, hasGuestAccess } from '../../utils/guestAccess'
import { useTranslation } from '../../utils/useTranslation'
import {
  LoginButton,
  PRICE_LOGIN_REQUIRED_TRIGGER_ID,
  SCROLL_AFTER_LOGIN_KEY,
  CONFIGURATOR_STATE_KEY,
} from '../login'
import settings from '@nobia/zeus-components/lib/settings'
import { useAuth } from '@nobia/zeus-components/lib/auth'

const LoginBar = styled.div`
  background-color: ${prop('theme.section.bgColor')};
  padding: 30px 15px;
  display: flex;
  justify-content: start;
  align-items: start;

  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    padding: 30px 90px;
  }
`
const Sticky = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${p => (hasMagnetSimplifiedVersion ? '#eed7c4' : p.theme.banner.bgColor)};
  color: ${p => (hasMagnetSimplifiedVersion ? '#black' : p.theme.banner.color)};
  width: 100%;
  padding: 22px ${prop('theme.body.padding')};
  text-align: left;
  font-weight: 500;
  display: none;
  z-index: 11;
  margin-top: 60px;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    display: block;
  }
`
const Wrapper = styled.div``
const Button = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${p => p.theme.banner.bgColor};
  color: ${p => p.theme.banner.color};
  width: 100%;
  padding: 20px ${prop('theme.body.padding')};
  margin: 0;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    background-color: ${p => p.theme.section.bgColor};
    color: ${theme('colors.primary')};
    pointer-events: none;
    display: ${p => (p.isEstimate ? 'none' : 'flex')};
    padding: 40px ${prop('theme.body.padding')} 0 ${prop('theme.body.padding')};
  }
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    padding: ${p => (p.isRange ? '60px 90px 0' : '30px 90px 0')};
  }
`
const H3 = styled.h3`
  margin: 0;
  color: ${theme('colors.white')};
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    color: ${theme('colors.primary')};
  }
`

const Content = styled.div`
  background-color: ${p => p.theme.section.bgColor};
  border: 1px solid ${p => p.theme.section.bgColor};
  padding-bottom: ${p => (p.isRange ? '50px' : '20px')};
  position: relative;
  ${p =>
    p.isEstimate &&
    `
    @media (min-width: ${prop('theme.body.tabletWidth')}) {
      padding-top: 20px;
    }
    `}
`
const IconWrapper = styled.div`
  background-color: ${theme('iconWrapper.bgColor')};
  color: ${theme('colors.primary')};
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    display: none;
  }
`

const Text = styled.span`
  color: ${theme('section.bgColor')};
  font-weight: 300;
`

const Mobile = styled.div`
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    display: none;
  }
`
const Desktop = styled.div`
  display: none;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    display: block;
  }
`
const EstimateDesktop = styled.div`
  display: none;
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    display: block;
  }
  @media (min-width: ${prop('theme.body.desktopWidth')}) {
    margin: 0 ${prop('theme.body.marginDesktop')};
  }
`
const EstimateMobile = styled(H3)`
  @media (min-width: ${prop('theme.body.tabletWidth')}) {
    display: none;
  }
`
const P = styled.p`
  opacity: ${p => p.opacity || 1};
  font-size: 22px;
  font-weight: 300;
  margin: 0;
`
const Strong = styled.strong`
  font-size: ${prop('theme.section.fontSize')};
  font-weight: ${prop('theme.section.fontWeight')};
`

const Section = ({
  id,
  type,
  title,
  description,
  cards,
  onSelect,
  sections,
  selection,
  summary,
  months,
  handleMonths,
  monthsToPayOptions,
  tooltip,
  setShowModal,
  financeOption,
  financeId,
  financeTitle,
  handleFinanceOption,
}) => {
  const { t, country } = useTranslation()
  const [showTooltip, setShowTooltip] = useState(false)
  const [isOpen, setIsOpen] = useState(true)
  const { profile } = useAuth()
  const isMagnetBrand = settings.brand === 'magnet'
  const isLoggedIn = !!profile?.email
  const [guestAccessGranted, setGuestAccessGranted] = useState(() => hasGuestAccess())
  const isShowSummary = !isMagnetBrand || isLoggedIn || guestAccessGranted

  useEffect(() => {
    const onGuestAccess = () => setGuestAccessGranted(hasGuestAccess())
    window.addEventListener(GUEST_ACCESS_EVENT, onGuestAccess)
    return () => window.removeEventListener(GUEST_ACCESS_EVENT, onGuestAccess)
  }, [])

  useEffect(() => {
    if (id === 'estimate') {
      const scrollTarget = localStorage.getItem(SCROLL_AFTER_LOGIN_KEY)
      if (scrollTarget === 'estimate') {
        setTimeout(() => {
          const yOffset = -85;
          const element = document.getElementById("estimate");
          const position = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: position, behavior: 'smooth' });
        }, 1000)
        localStorage.removeItem(SCROLL_AFTER_LOGIN_KEY)
      }
    }
  }, [id])

  const scrollToEstimate = () => {
    const element = document.getElementById("estimate");
    element.scrollIntoView();
  }

  const handleLogin = () => {
    localStorage.setItem(
      CONFIGURATOR_STATE_KEY,
      JSON.stringify({ sections, selection })
    )
    localStorage.setItem(SCROLL_AFTER_LOGIN_KEY, 'estimate')
  }

  return (
    <>
      {id === 'estimate' && (
        <>
          {!isShowSummary && (
            <LoginBar>
              <LoginButton id={PRICE_LOGIN_REQUIRED_TRIGGER_ID} onClick={handleLogin}>
                {t('app.components.login.button')}
              </LoginButton>
            </LoginBar>
          )}
          {isShowSummary && (
            <Sticky>
              <EstimateDesktop onClick={scrollToEstimate}>
                <P>
                  {t('app.components.cost-summary.title')}{' '}
                  <Strong>{formatPrice({ price: summary, country })}</Strong>{' '}
                  {hasMonthlyPayment && (
                    <>
                      <Strong>
                        {hasFullVersion ? (
                          <>
                            {t('app.components.cost-summary.alternative')}{' '}
                            {formatPrice({
                              price: getMonthlyPayment(summary, months, financeId),
                              country,
                              digits: 2,
                            })}{' '}
                            {t('app.components.cost-summary.per-month')}
                          </>
                        ) : (
                          <>
                            <Text>
                              {t('app.components.cost-summary.alternative')}{' '}
                            </Text>
                            {formatPrice({
                              price: getMonthlyPaymentShortVersion(summary),
                              country,
                            })}
                            {t('app.components.cost-summary.per-month')}
                            <Text>
                              {' '}
                              {t('app.components.cost-summary.in')}
                              {' '}
                              {financeOption.monthsToPay[0].months}
                              {' '}
                              {t('app.components.cost-summary.months')}
                            </Text>
                          </>
                        )}
                      </Strong>
                    </>
                  )}
                </P>
              </EstimateDesktop>
            </Sticky>
          )}
        </>
      )}
      <Wrapper id={id} isEstimate={id === 'estimate'}>
        {(id !== 'estimate' || isShowSummary) && (<Button
          onClick={() => setIsOpen(!isOpen)}
          isEstimate={id === 'estimate'}
          isRange={id === 'range'}
        >
          {id === 'estimate' ? (
            <>
              <EstimateDesktop>
                <P>
                  {t('app.components.cost-summary.title')}{' '}
                  <Strong>{formatPrice({ price: summary, country })}</Strong>
                  {hasMonthlyPayment && (
                    <>
                      {' '}
                      {t('app.components.cost-summary.alternative')}{' '}
                      <Strong>
                        {hasFullVersion ? (
                          <>
                            {formatPrice({
                              price: getMonthlyPayment(summary, months, financeId),
                              country,
                              digits: 2,
                            })}{' '}
                          </>
                        ) : (
                          <>
                            {formatPrice({
                              price: getMonthlyPaymentShortVersion(summary),
                              country,
                            })}
                          </>
                        )}
                        {t('app.components.cost-summary.per-month')}
                      </Strong>
                    </>
                  )}
                </P>
              </EstimateDesktop>
              <EstimateMobile>{title}</EstimateMobile>
            </>
          ) : (
            <H3>{title}</H3>
          )}

          <IconWrapper>
            <OpenCloseIcon isClosed={isOpen} size={16} />
          </IconWrapper>
        </Button>)}
        <Suspense fallback={<Loader />}>
          <>
            <Mobile>
              <Accordion id={id} isOpen={isOpen} showTooltip={showTooltip}>
                {id === 'estimate' ? (
                  <Content>
                    {isShowSummary && (
                      <Summary
                        sections={sections}
                        selection={selection}
                        onSelect={onSelect}
                        summary={summary}
                        months={months}
                        handleMonths={handleMonths}
                        setShowModal={setShowModal}
                        financeOption={financeOption}
                        financeId={financeId}
                        financeTitle={financeTitle}
                        handleFinanceOption={handleFinanceOption}
                        monthsToPayOptions={monthsToPayOptions}
                      />
                    )}
                  </Content>
                ) : (
                  <Cards
                    id={id}
                    type={type}
                    title={title}
                    description={description}
                    cards={cards}
                    onSelect={onSelect}
                    selection={selection}
                    isOpen={isOpen}
                    tooltip={tooltip}
                    showTooltip={showTooltip}
                    toggleTooltip={value => setShowTooltip(value)}
                  />
                )}
              </Accordion>
            </Mobile>
            <Desktop>
              {id === 'estimate' ? (
                <Content isEstimate>
                  {isShowSummary && (
                    <Summary
                      sections={sections}
                      selection={selection}
                      onSelect={onSelect}
                      summary={summary}
                      months={months}
                      handleMonths={handleMonths}
                      monthsToPayOptions={monthsToPayOptions}
                      setShowModal={setShowModal}
                      handleFinanceOption={handleFinanceOption}
                      financeOption={financeOption}
                      financeId={financeId}
                      financeTitle={financeTitle}
                    />
                  )}
                </Content>
              ) : (
                <Cards
                  id={id}
                  type={type}
                  title={title}
                  description={description}
                  cards={cards}
                  onSelect={onSelect}
                  selection={selection}
                  isOpen={isOpen}
                  tooltip={tooltip}
                  showTooltip={showTooltip}
                  toggleTooltip={value => setShowTooltip(value)}
                />
              )}
            </Desktop>
          </>
        </Suspense>
      </Wrapper>
    </>
  )
}

Section.defaultProps = {
  cards: [],
}

Section.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  cards: PropTypes.arrayOf(PropTypes.shape({ offsetWidth: PropTypes.string })),
  onSelect: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selection: PropTypes.shape({}).isRequired,
  summary: PropTypes.number.isRequired,
  months: PropTypes.shape({
    months: PropTypes.number.isRequired,
  }).isRequired,
  handleMonths: PropTypes.func.isRequired,
  monthsToPayOptions: PropTypes.arrayOf(
    PropTypes.shape({ repaymentFactor: PropTypes.number.isRequired, months: PropTypes.number.isRequired })
  ).isRequired,
  tooltip: PropTypes.shape({}).isRequired,
  financeOption: PropTypes.shape({
    monthsToPay: PropTypes.arrayOf(
      PropTypes.shape({
        months: PropTypes.number.isRequired,
      })
    ).isRequired,
  }).isRequired,
  financeId: PropTypes.string.isRequired,
  financeTitle: PropTypes.string.isRequired,
  handleFinanceOption: PropTypes.func.isRequired,
  setShowModal: PropTypes.func.isRequired,
}

export default Section
